import { ActionIcon, Alert, Anchor, Badge, Box, Breadcrumbs, Button, Card, Code as MCode, FileButton, Group, Modal, NumberInput, Progress, ScrollArea, SimpleGrid, Stack, Switch, Text, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconFileUpload, IconMapPinOff, IconPencil, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api } from '../api/client';
import type { LocationRow } from '../api/types';
import { PageActions } from '../components/page-actions';
import { useToast } from '../components/Toast';
import { isEn, nameOf, tr } from '../lib/i18n';
import { matches } from '../lib/search';
import { bi, BilingualField, BilingualScope, BilingualToolbar, missingLang, useBilingualScope, type Bilingual } from '../ui/bilingual';
import { Loading, SearchInput } from '../ui/kit';

type Level = 'countries' | 'governorates' | 'areas' | 'neighborhoods';
const LEVELS: { level: Level; title: string; child: string }[] = [
  { level: 'countries', title: tr('البلاد', 'Countries'), child: tr('دولة', 'country') },
  { level: 'governorates', title: tr('المحافظات', 'Governorates'), child: tr('محافظة', 'governorate') },
  { level: 'areas', title: tr('المناطق', 'Areas'), child: tr('منطقة', 'area') },
  { level: 'neighborhoods', title: tr('الأحياء', 'Neighborhoods'), child: tr('حي', 'neighborhood') },
];

interface Draft {
  id?: number;
  level: Level;
  parentId?: number;
  name: Bilingual;
  sortOrder: number;
  isActive: boolean;
  iso2?: string;
  phoneCode?: string;
  currency?: string;
  /** «lat, lng» as typed (or a pasted Google Maps link) */
  point?: string;
  pointBefore?: string;
}

const pointText = (r: LocationRow) => (r.latitude != null && r.longitude != null ? `${Number(r.latitude)}, ${Number(r.longitude)}` : '');
/** «31.95, 35.91» or a Google Maps link → the point */
function parsePoint(s: string): { lat: number; lng: number } | null {
  const t = decodeURIComponent(s.trim());
  const m = t.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/) ?? t.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/) ?? t.match(/[?&](?:q|query|ll)=(-?\d+\.\d+),\s*(-?\d+\.\d+)/) ?? t.match(/^(-?\d{1,2}(?:\.\d+)?)\s*[,،]\s*(-?\d{1,3}(?:\.\d+)?)$/);
  if (!m) return null;
  const lat = Number(m[1]);
  const lng = Number(m[2]);
  return Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
}

export function LocationsPage() {
  const [sel, setSel] = useState<(LocationRow | undefined)[]>([undefined, undefined, undefined]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const qc = useQueryClient();
  const { toast, toastError } = useToast();

  const select = (idx: number, r: LocationRow) => setSel((s) => s.map((v, i) => (i < idx ? v : i === idx ? r : undefined)));

  const save = useMutation({
    mutationFn: (d: Draft) => {
      const body: Record<string, unknown> = { nameAr: d.name.ar.trim(), nameEn: d.name.en.trim(), sortOrder: Number(d.sortOrder) || 0, isActive: d.isActive };
      if (d.level === 'countries') Object.assign(body, { phoneCode: d.phoneCode, currency: d.currency?.toUpperCase(), ...(d.id ? {} : { iso2: d.iso2?.toUpperCase() }) });
      if (!d.id && d.level !== 'countries') body.parentId = d.parentId;
      if (d.id && d.level !== 'countries' && (d.point ?? '') !== (d.pointBefore ?? '')) {
        const p = d.point?.trim() ? parsePoint(d.point) : null;
        if (d.point?.trim() && !p) throw new Error(tr('الإحداثيات غير صحيحة — اكتبها هكذا: 31.95, 35.91 أو الصق رابط خرائط Google', 'Invalid point — write it like 31.95, 35.91 or paste a Google Maps link'));
        body.point = p;
      }
      return d.id ? api(`/${d.level}/${d.id}`, { method: 'PATCH', body }) : api(`/${d.level}`, { method: 'POST', body });
    },
    onSuccess: (_, d) => {
      toast(tr('تم الحفظ', 'Saved'));
      setDraft(null);
      qc.invalidateQueries({ queryKey: ['loc', d.level] });
    },
    onError: toastError,
  });

  const path = sel.filter(Boolean) as LocationRow[];

  return (
    <>
      <PageActions>
        <Button variant="default" leftSection={<IconFileUpload size={16} />} onClick={() => setImportOpen(true)}>{tr('استيراد CSV', 'Import CSV')}</Button>
      </PageActions>
      <GeocodeCard />
      <Card padding={0}>
        <Group justify="space-between" px="lg" py="md" gap="sm" style={{ borderBottom: '1px solid var(--pf-line)' }}>
          <Box>
            <Text fw={600}>{tr('البلاد والمواقع', 'Countries & locations')}</Text>
            <Breadcrumbs separator={isEn() ? <IconChevronRight size={12} /> : <IconChevronLeft size={12} />} separatorMargin={6} mt={4}>
              {path.length === 0
                ? [<Text key="hint" size="sm" c="dimmed">{tr('اختر دولة لتصفّح محافظاتها ومناطقها وأحيائها', 'Pick a country to browse its governorates, areas and neighborhoods')}</Text>]
                : path.map((r) => <Text key={r.id} size="sm" c="dimmed">{nameOf(r)}</Text>)}
            </Breadcrumbs>
          </Box>
        </Group>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing={0}>
          {LEVELS.map((l, idx) => {
            const parentId = idx === 0 ? undefined : sel[idx - 1]?.id;
            return (
              <LevelColumn
                key={l.level}
                level={l.level}
                title={l.title}
                parentId={parentId}
                parentName={idx === 0 ? undefined : sel[idx - 1] ? nameOf(sel[idx - 1]) : undefined}
                prevTitle={idx > 0 ? LEVELS[idx - 1]!.child : ''}
                disabled={idx > 0 && !parentId}
                selected={idx < 3 ? sel[idx]?.id : undefined}
                onSelect={idx < 3 ? (r) => select(idx, r) : undefined}
                onAdd={() => setDraft({ level: l.level, parentId, name: bi(), sortOrder: 0, isActive: true })}
                onEdit={(r) => setDraft({ id: r.id, level: l.level, name: bi(r.name_ar, r.name_en), sortOrder: r.sort_order, isActive: r.is_active, iso2: r.iso2, phoneCode: r.phone_code, currency: r.currency, point: pointText(r), pointBefore: pointText(r) })}
                addLabel={tr(`إضافة ${l.child}`, `Add ${l.child}`)}
              />
            );
          })}
        </SimpleGrid>
      </Card>

      <Modal opened={!!draft} onClose={() => setDraft(null)} size={440} title={draft?.id ? tr('تعديل', 'Edit') : LEVELS.find((l) => l.level === draft?.level) ? tr(`إضافة ${LEVELS.find((l) => l.level === draft?.level)!.child}`, `Add ${LEVELS.find((l) => l.level === draft?.level)!.child}`) : ''}>
        <BilingualScope key={draft?.id ?? `new-${draft?.level}`}>
          {draft && (
            <LocForm name={draft.name} onSubmit={() => save.mutate(draft)}>
              <BilingualField label={tr('الاسم', 'Name')} value={draft.name} onChange={(name) => setDraft({ ...draft, name })} data-autofocus />
              {draft.level === 'countries' && (
                <SimpleGrid cols={3} spacing="sm">
                  <TextInput withAsterisk label={tr('رمز ISO', 'ISO code')} value={draft.iso2 ?? ''} disabled={!!draft.id} dir="ltr" placeholder="JO" onChange={(e) => setDraft({ ...draft, iso2: e.currentTarget.value.toUpperCase().slice(0, 2) })} />
                  <TextInput withAsterisk label={tr('مفتاح الهاتف', 'Phone code')} value={draft.phoneCode ?? ''} dir="ltr" placeholder="+962" onChange={(e) => setDraft({ ...draft, phoneCode: e.currentTarget.value })} />
                  <TextInput withAsterisk label={tr('العملة', 'Currency')} value={draft.currency ?? ''} dir="ltr" placeholder="JOD" onChange={(e) => setDraft({ ...draft, currency: e.currentTarget.value.toUpperCase().slice(0, 3) })} />
                </SimpleGrid>
              )}
              {draft.id && draft.level !== 'countries' && (
                <TextInput
                  label={tr('الموقع على الخريطة', 'Point on the map')}
                  description={tr('خط العرض، خط الطول — أو الصق رابط خرائط Google. يُستخدم لوضع العقارات تقريبياً على الخريطة.', 'Latitude, longitude — or paste a Google Maps link. Used to place properties approximately on the map.')}
                  placeholder="31.9539, 35.9106"
                  dir="ltr"
                  value={draft.point ?? ''}
                  onChange={(e) => setDraft({ ...draft, point: e.currentTarget.value })}
                  rightSection={parsePoint(draft.point ?? '') ? (
                    <Anchor size="xs" href={`https://www.google.com/maps/search/?api=1&query=${parsePoint(draft.point ?? '')!.lat},${parsePoint(draft.point ?? '')!.lng}`} target="_blank" rel="noreferrer">{tr('تحقق', 'Check')}</Anchor>
                  ) : undefined}
                  rightSectionWidth={56}
                />
              )}
              <Group gap="lg" align="flex-end">
                <NumberInput w={110} label={tr('الترتيب', 'Sort order')} value={draft.sortOrder} allowDecimal={false} dir="ltr" onChange={(v) => setDraft({ ...draft, sortOrder: Number(v) || 0 })} />
                <Switch mb={8} checked={draft.isActive} onChange={(e) => setDraft({ ...draft, isActive: e.currentTarget.checked })} label={tr('نشط', 'Active')} />
              </Group>
              <Group justify="flex-end" gap="xs" mt="xs">
                <Button variant="default" onClick={() => setDraft(null)}>{tr('إلغاء', 'Cancel')}</Button>
                <Button type="submit" loading={save.isPending}>{tr('حفظ', 'Save')}</Button>
              </Group>
            </LocForm>
          )}
        </BilingualScope>
      </Modal>
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} />
    </>
  );
}

function LevelColumn(p: {
  level: Level;
  title: string;
  parentId?: number;
  parentName?: string;
  prevTitle: string;
  disabled: boolean;
  selected?: number;
  onSelect?: (r: LocationRow) => void;
  onAdd: () => void;
  onEdit: (r: LocationRow) => void;
  addLabel: string;
}) {
  const [q, setQ] = useState('');
  const list = useQuery({
    queryKey: ['loc', p.level, p.parentId],
    queryFn: () => api<{ items: LocationRow[] }>(`/${p.level}`, { query: { parentId: p.parentId, includeInactive: true } }),
    enabled: !p.disabled,
  });
  const all = list.data?.items ?? [];
  const items = all.filter((r) => matches(q.trim().toLowerCase(), r.name_ar, r.name_en));
  const missing = all.filter((r) => !r.name_en?.trim()).length;
  const Chevron = isEn() ? IconChevronRight : IconChevronLeft;
  return (
    <Box h={{ base: 380, lg: 540 }} style={{ borderInlineStart: '1px solid var(--pf-line)', display: 'flex', flexDirection: 'column', minWidth: 0 }} className="pf-loc-col">
      <Box px="md" pt="md" pb="xs">
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Box style={{ minWidth: 0 }}>
            <Text fw={600} size="sm">{p.title}</Text>
            <Text size="xs" c="dimmed" truncate>
              {p.disabled ? '—' : list.data ? (
                <>
                  {tr(`${all.length} عنصر`, `${all.length} items`)}
                  {p.parentName ? tr(` في ${p.parentName}`, ` in ${p.parentName}`) : ''}
                  {missing ? <Text span c="orange.7" size="xs">{tr(` · ${missing} بدون إنجليزي`, ` · ${missing} missing English`)}</Text> : null}
                </>
              ) : ' '}
            </Text>
          </Box>
          <Tooltip label={p.addLabel}>
            <ActionIcon variant="light" color="ink" disabled={p.disabled} onClick={p.onAdd} aria-label={p.addLabel}><IconPlus size={16} /></ActionIcon>
          </Tooltip>
        </Group>
        <SearchInput mt="sm" w="100%" size="xs" value={q} onChange={(e) => setQ(e.currentTarget.value)} disabled={p.disabled} placeholder={tr('بحث…', 'Search…')} />
      </Box>
      <ScrollArea style={{ flex: 1 }} type="auto" px={6} pb={6}>
        {p.disabled ? (
          <Stack align="center" gap={6} py={60} px="md" ta="center">
            <IconMapPinOff size={22} stroke={1.5} color="var(--pf-faint, #9aa1ab)" />
            <Text size="xs" c="dimmed">{tr(`اختر ${p.prevTitle} أولاً`, `Pick a ${p.prevTitle} first`)}</Text>
          </Stack>
        ) : list.isLoading ? (
          <Loading h={120} />
        ) : items.length === 0 ? (
          <Text size="xs" c="dimmed" ta="center" py={40}>{q ? tr('لا نتائج', 'No results') : tr('فارغ — أضف أول عنصر', 'Empty — add the first one')}</Text>
        ) : (
          items.map((r) => {
            const active = p.selected === r.id;
            return (
              <UnstyledButton
                key={r.id}
                onClick={() => p.onSelect?.(r)}
                w="100%"
                px="sm"
                py={7}
                className="pf-row"
                style={{ display: 'flex', alignItems: 'center', gap: 8, borderRadius: 8, background: active ? 'var(--pf-selected)' : undefined, opacity: r.is_active ? 1 : 0.5, cursor: p.onSelect ? 'pointer' : 'default' }}
              >
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={active ? 600 : 500} truncate lh={1.35}>{r.name_ar}</Text>
                  <Text size="xs" c={r.name_en?.trim() ? 'dimmed' : 'orange.7'} truncate lh={1.35} dir={r.name_en?.trim() ? 'ltr' : undefined} ta="start">
                    {r.name_en?.trim() || tr('بدون إنجليزي', 'No English')}
                    {r.iso2 ? ` · ${r.iso2} · ${r.currency}` : ''}
                    {!r.is_active ? tr(' · موقوف', ' · Inactive') : ''}
                  </Text>
                </Box>
                <ActionIcon size="sm" variant="subtle" color="gray" onClick={(e) => { e.stopPropagation(); p.onEdit(r); }} aria-label={tr('تعديل', 'Edit')}>
                  <IconPencil size={14} />
                </ActionIcon>
                {p.onSelect && <Chevron size={15} color={active ? 'var(--pf-text)' : 'var(--pf-line-strong)'} />}
              </UnstyledButton>
            );
          })
        )}
      </ScrollArea>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// CSV import (UC-04): header row required
// countryIso2,governorateAr,governorateEn,areaAr,areaEn,neighborhoodAr,neighborhoodEn
// (English columns are optional; rows without them are flagged as "missing English")
// ---------------------------------------------------------------------------
interface GeoState { running: boolean; scope: string | null; level: string | null; done: number; total: number; found: number; notFound: number; finishedAt: string | null; error: string | null }
interface GeoCoverage { level: string; total: number; withPoint: number; notFound: number; manual: number }
const LEVEL_NAME: Record<string, string> = { governorates: tr('المحافظات', 'Governorates'), areas: tr('المناطق', 'Areas'), neighborhoods: tr('الأحياء', 'Neighborhoods') };

/**
 * «نقاط المواقع على الخريطة»: how many governorates / areas / neighborhoods have a point, and the button that looks them
 * up in OpenStreetMap (in the background, one per second). The points place properties without an exact pin.
 */
function GeocodeCard() {
  const { toast, toastError } = useToast();
  const q = useQuery({
    queryKey: ['loc', 'geocode'],
    queryFn: () => api<{ state: GeoState; coverage: GeoCoverage[] }>('/locations/geocode'),
    refetchInterval: (query) => (query.state.data?.state.running ? 3000 : false),
  });
  const start = useMutation({
    mutationFn: (scope: 'used' | 'all') => api('/locations/geocode', { method: 'POST', body: { scope } }),
    onSuccess: () => {
      toast(tr('بدأ البحث عن الإحداثيات — يعمل في الخلفية', 'Looking up points — running in the background'));
      void q.refetch();
    },
    onError: toastError,
  });
  const s = q.data?.state;
  return (
    <Card mb="md">
      <Group justify="space-between" align="flex-start" gap="md">
        <Box style={{ flex: 1, minWidth: 260 }}>
          <Text fw={600}>{tr('نقاط المواقع على الخريطة', 'Map points of locations')}</Text>
          <Text size="sm" c="dimmed" mt={2}>
            {tr('تضع العقارات التي لا تملك موقعاً دقيقاً في منتصف حيّها أو منطقتها، وتُظهر الطلبات كطلب على المناطق. تُجلب مرة واحدة من OpenStreetMap ويمكن تعديل أي نقطة يدوياً.', 'Places properties without an exact pin in the middle of their neighborhood / area, and shows requests as demand per area. Looked up once in OpenStreetMap; any point can be fixed by hand.')}
          </Text>
          <Group gap="xs" mt="sm">
            {(q.data?.coverage ?? []).map((c) => (
              <Badge key={c.level} variant="light" color={c.withPoint === c.total ? 'green' : 'gray'} size="lg" radius="sm">
                {LEVEL_NAME[c.level] ?? c.level}: {c.withPoint}/{c.total}{c.notFound ? ` · ${tr('لم يوجد', 'not found')} ${c.notFound}` : ''}
              </Badge>
            ))}
          </Group>
        </Box>
        <Group gap="xs">
          <Button loading={start.isPending} disabled={s?.running} onClick={() => start.mutate('used')}>{tr('تعبئة المستخدمة', 'Fill used ones')}</Button>
          <Button variant="default" loading={start.isPending} disabled={s?.running} onClick={() => start.mutate('all')}>{tr('تعبئة الكل', 'Fill all')}</Button>
        </Group>
      </Group>
      {s?.running && (
        <Box mt="md">
          <Text size="sm" mb={6}>
            {tr(`جارٍ البحث: ${LEVEL_NAME[s.level ?? ''] ?? '…'} ${s.done}/${s.total} — وُجد ${s.found}، لم يوجد ${s.notFound}`, `Looking up ${LEVEL_NAME[s.level ?? ''] ?? '…'} ${s.done}/${s.total} — found ${s.found}, not found ${s.notFound}`)}
          </Text>
          <Progress value={s.total ? (s.done / s.total) * 100 : 0} animated />
        </Box>
      )}
      {!s?.running && s?.error && <Alert mt="md" color="red">{s.error}</Alert>}
      <Text size="xs" c="dimmed" mt="sm">{tr('بيانات المواقع © مساهمو OpenStreetMap', 'Location data © OpenStreetMap contributors')}</Text>
    </Card>
  );
}

function parseCsv(text: string): Record<string, string>[] {
  const rows: string[][] = [];
  let cur: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') quoted = false;
      else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') { cur.push(field); field = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      cur.push(field); field = '';
      if (cur.some((x) => x.trim())) rows.push(cur);
      cur = [];
    } else field += c;
  }
  cur.push(field);
  if (cur.some((x) => x.trim())) rows.push(cur);
  const [header, ...data] = rows;
  if (!header) return [];
  const keys = header.map((h) => h.trim().replace(/^﻿/, ''));
  return data.map((r) => Object.fromEntries(keys.map((k, i) => [k, (r[i] ?? '').trim()]).filter(([, v]) => v !== '')));
}

interface ImportResult {
  dryRun: boolean;
  total: number;
  imported: number;
  created: { governorates: number; areas: number; neighborhoods: number };
  errors: { row: number; message: string }[];
}

function ImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);
  const run = useMutation({
    mutationFn: (dryRun: boolean) => api<ImportResult>('/locations/import', { method: 'POST', body: { dryRun, rows } }),
    onSuccess: (r) => {
      setResult(r);
      if (!r.dryRun) {
        toast(tr(`تم استيراد ${r.imported} صف`, `${r.imported} row(s) imported`));
        qc.invalidateQueries({ queryKey: ['loc'] });
      }
    },
    onError: toastError,
  });
  const close = () => {
    setRows([]);
    setResult(null);
    setFileName('');
    onClose();
  };
  const sample = 'countryIso2,governorateAr,governorateEn,areaAr,areaEn,neighborhoodAr,neighborhoodEn\nJO,عمّان,Amman,وادي السير,Wadi Al-Seer,الصويفية,Sweifieh';
  return (
    <Modal opened={open} onClose={close} size={600} title={tr('استيراد المواقع من CSV', 'Import locations from CSV')}>
      <Stack gap="md">
        <Text size="sm">{tr('الصف الأول أسماء الأعمدة. أعمدة En اختيارية — وتُكمل الترجمة الناقصة للمواقع الموجودة:', 'The first row holds the column names. The En columns are optional and fill in missing English names for existing locations:')}</Text>
        <MCode block dir="ltr" style={{ fontSize: 12 }}>{sample}</MCode>
        <FileButton accept=".csv,text/csv" onChange={async (f) => { if (f) { setRows(parseCsv(await f.text())); setFileName(f.name); setResult(null); } }}>
          {(props) => <Button {...props} variant="default" leftSection={<IconFileUpload size={16} />}>{fileName || tr('اختيار ملف CSV (UTF-8)', 'Choose CSV file (UTF-8)')}</Button>}
        </FileButton>
        {rows.length > 0 && !result && <Alert color="blue" variant="light">{tr(`تمت قراءة ${rows.length} صف. افحص أولاً ثم استورد.`, `${rows.length} row(s) read. Check first, then import.`)}</Alert>}
        {result && (
          <Alert color={result.errors.length ? 'orange' : 'teal'} variant="light" title={result.dryRun ? tr('نتيجة الفحص', 'Check result') : tr('تم الاستيراد', 'Imported')}>
            <Text size="sm">
              {tr(
                `${result.imported} صف صالح من ${result.total} — محافظات جديدة ${result.created.governorates}، مناطق ${result.created.areas}، أحياء ${result.created.neighborhoods}`,
                `${result.imported} of ${result.total} rows valid — new governorates ${result.created.governorates}, areas ${result.created.areas}, neighborhoods ${result.created.neighborhoods}`,
              )}
            </Text>
            {result.errors.slice(0, 20).map((e) => <Text key={e.row} size="xs" mt={4}>{tr(`صف ${e.row}: `, `Row ${e.row}: `)}{e.message}</Text>)}
          </Alert>
        )}
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={close}>{tr('إغلاق', 'Close')}</Button>
          <Button variant="light" color="ink" disabled={!rows.length} loading={run.isPending && run.variables === true} onClick={() => run.mutate(true)}>{tr('فحص', 'Check')}</Button>
          <Button disabled={!rows.length || !result?.dryRun} loading={run.isPending && run.variables === false} onClick={() => run.mutate(false)}>{tr('استيراد', 'Import')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function LocForm({ name, onSubmit, children }: { name: Bilingual; onSubmit: () => void; children: ReactNode }) {
  const scope = useBilingualScope();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const miss = missingLang(name);
        if (miss) return scope?.setLang(miss);
        onSubmit();
      }}
    >
      <Stack gap="md">
        <BilingualToolbar values={[{ value: name }]} />
        {children}
      </Stack>
    </form>
  );
}
